import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { isAddress } from 'viem';

import { environment } from 'src/environments/environment';
import { StateService } from '@/services/state.service';
import { Web3Service } from '@/services/web3.service';
import { isLocalDevMimicEnabled } from '@/utils/local-mimic';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  selector: 'app-mimic-panel',
  templateUrl: './mimic-panel.component.html',
  styleUrls: ['./mimic-panel.component.scss'],
})
export class MimicPanelComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  expanded = false;
  hidden = false;
  addressInput = '';
  error = '';

  /** Visible on localhost (local mimic) or when the connected wallet is an admin. */
  visible$ = combineLatest([
    this.stateSvc.web3Connected$,
    this.stateSvc.realWalletAddress$,
  ]).pipe(
    map(([connected, realAddr]) => {
      if (isLocalDevMimicEnabled()) {
        return true;
      }
      if (!connected || !realAddr) {
        return false;
      }
      return (environment.adminWallets ?? []).some(
        a => a.toLowerCase() === realAddr.toLowerCase(),
      );
    }),
    takeUntil(this.destroy$),
  );

  isMimicking$ = this.stateSvc.isMimicking$;
  mimicAddress$ = this.stateSvc.mimicAddress$;

  constructor(
    private stateSvc: StateService,
    private web3Svc: Web3Service,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle(): void {
    this.expanded = !this.expanded;
    this.error = '';
  }

  hide(): void {
    this.hidden = true;
    this.expanded = false;
  }

  show(): void {
    this.hidden = false;
  }

  async activate(): Promise<void> {
    const addr = this.addressInput.trim();
    if (!isAddress(addr)) {
      this.error = 'Invalid Ethereum address';
      return;
    }
    this.error = '';
    await this.web3Svc.activateMimic(addr);
    this.addressInput = '';
    this.expanded = false;
  }

  deactivate(): void {
    this.web3Svc.deactivateMimic();
    this.expanded = false;
  }

  truncate(address: string): string {
    if (!address || address.length < 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }
}
